<template>
  <div>
    <div class="mb-20 flex items-center">
      <div>
        <h2
          class="text-neutral-text-strong font-bold text-lg"
          data-testid="title"
        >
          Standard Populations
        </h2>
        <p class="text-neutral-text" data-testid="subtitle">
          For an optimal use of Crossbeam, define your Customers, your
          Prospects, and your Open Opportunities
        </p>
      </div>
    </div>
    <ListContainer
      :list="popsWithDefaults"
      :get-key="(population: Population) => population.id"
      :has-divider="() => false"
      :show-hover="true"
      class="c-list-container--new-modal"
    >
      <template #header>
        <div
          class="border-b border-solid border-neutral-border bg-neutral-background-weak rounded-tr-16 rounded-tl-16 px-4 py-8 flex items-center w-full"
        >
          <span class="text-sm font-bold text-neutral-text px-12 flex-1">
            Population Name
          </span>
          <span
            v-if="!isOfflinePartner"
            class="text-sm font-bold text-neutral-text px-12 w-[220px] shrink-0"
          >
            Sharing Setting
          </span>
          <span
            class="text-sm font-bold text-neutral-text px-12 w-[220px] shrink-0"
          >
            Records Count
          </span>
        </div>
      </template>
      <template #default="{ item: population }">
        <PopulationItem
          :population
          :at-or-above-cap
          :is-offline-partner="isOfflinePartner"
          :offline-partner-uuid="offlinePartnerUuid"
          @pop-clicked="emit('population-clicked', population)"
          @create-population="emit('create-population', population)"
          @edit-population="emit('edit-population', population)"
          @view-population="emit('view-population', population)"
        />
      </template>
    </ListContainer>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';

import ListContainer from '@/components/ListContainer.vue';
import PopulationItem from '@/components/populations/PopulationItem.vue';

import { Population, PopulationStats } from '@/types/populations';

const {
  populations = [],
  isOfflinePartner = false,
  offlinePartnerUuid,
} = defineProps<{
  populations?: (Population & { meta?: PopulationStats })[];
  atOrAboveCap: boolean;
  isOfflinePartner?: boolean;
  offlinePartnerUuid?: string;
}>();
const emit = defineEmits<{
  'population-clicked': [population: Population];
  'create-population': [population: Population];
  'edit-population': [population: Population];
  'view-population': [population: Population];
}>();

const standardDefaults = [
  {
    id: -1,
    name: 'Customers',
    tag: 'Required',
    isEmpty: true,
    standard_type: 'customers',
  },
  {
    id: -2,
    name: 'Open Opportunities',
    tag: 'Recommended',
    isEmpty: true,
    standard_type: 'open_opportunities',
  },
  {
    id: -3,
    name: 'Prospects',
    tag: 'Recommended',
    isEmpty: true,
    standard_type: 'prospects',
  },
];
function addDefaultStandardPopulations(populations: Population[]) {
  const populationMap = new Map(populations.map((pop) => [pop.name, pop]));
  return [
    ...standardDefaults.map((defaultPop) =>
      populationMap.has(defaultPop.name)
        ? (populationMap.get(defaultPop.name) as Population)
        : defaultPop,
    ),
    ...populations.filter(
      (pop) => !standardDefaults.some((def) => def.name === pop.name),
    ),
  ] as Population[];
}
const popsWithDefaults = computed<Population[]>(() =>
  addDefaultStandardPopulations(populations),
);
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-list-container--new-modal {
  :deep(.item--first) {
    @apply rounded-tr-none rounded-tl-none;
  }

  :deep(.c-bitts-divider) {
    border-color: color-mix(
      in srgb,
      theme(colors.neutral-border) 50%,
      transparent
    );
  }
}
</style>
